"use client"

import type * as React from "react"
import CloseIcon from "@/assets/svg/close-icon.svg"
import { useIsMobile } from "@/hooks/use-mobile"
import { cn } from "@/lib/utils"
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "./dialog"
import {
	Sheet,
	SheetClose,
	SheetContent,
	SheetDescription,
	SheetFooter,
	SheetHeader,
	SheetTitle,
	SheetTrigger,
} from "./sheet"

interface ResponsiveDialogProps {
	children?: React.ReactNode
	open?: boolean
	onOpenChange?: (open: boolean) => void
	modal?: boolean
}

interface ResponsiveDialogContentProps {
	className?: string
	children?: React.ReactNode
	onInteractOutside?: (event: Event) => void
}

interface ResponsiveDialogHeaderProps {
	className?: string
	children?: React.ReactNode
}

interface ResponsiveDialogFooterProps {
	className?: string
	children?: React.ReactNode
}

interface ResponsiveDialogTitleProps {
	className?: string
	children?: React.ReactNode
}

interface ResponsiveDialogDescriptionProps {
	className?: string
	children?: React.ReactNode
}

interface ResponsiveDialogTriggerProps {
	className?: string
	children?: React.ReactNode
	asChild?: boolean
}

function ResponsiveDialog({
	children,
	modal = true,
	...props
}: ResponsiveDialogProps) {
	const isMobile = useIsMobile()

	if (isMobile) {
		return <Sheet {...props}>{children}</Sheet>
	}

	return (
		<Dialog modal={modal} {...props}>
			{children}
		</Dialog>
	)
}

function ResponsiveDialogTrigger({
	children,
	...props
}: ResponsiveDialogTriggerProps) {
	const isMobile = useIsMobile()

	if (isMobile) {
		return <SheetTrigger {...props}>{children}</SheetTrigger>
	}

	return <DialogTrigger {...props}>{children}</DialogTrigger>
}

function ResponsiveDialogContent({
	className,
	children,
	onInteractOutside,
	...props
}: ResponsiveDialogContentProps) {
	const isMobile = useIsMobile()

	if (isMobile) {
		return (
			<SheetContent
				side="bottom"
				showCloseButton={false}
				className={cn(
					"px-5 pb-5 focus:outline-none h-[80dvh] gap-0",
					className,
				)}
				onInteractOutside={onInteractOutside}
				{...props}
			>
				<SheetClose className="absolute right-3 top-3 z-10">
					<CloseIcon className="size-5.5" />
					<span className="sr-only">Close</span>
				</SheetClose>
				{children}
			</SheetContent>
		)
	}

	return (
		<DialogContent
			className={cn("pt-0 px-5 pb-5 md:max-w-[484px] gap-0", className)}
			onInteractOutside={onInteractOutside}
			{...props}
		>
			{children}
		</DialogContent>
	)
}

function ResponsiveDialogHeader({
	className,
	children,
	...props
}: ResponsiveDialogHeaderProps) {
	const isMobile = useIsMobile()
	const headerClass = "pt-11 pb-4 px-0 active: focus:outline-none"

	if (isMobile) {
		return (
			<SheetHeader className={cn(headerClass, className)} {...props}>
				{children}
			</SheetHeader>
		)
	}

	return (
		<DialogHeader className={cn(headerClass, className)} {...props}>
			{children}
		</DialogHeader>
	)
}

function ResponsiveDialogFooter({
	className,
	children,
	...props
}: ResponsiveDialogFooterProps) {
	const isMobile = useIsMobile()

	if (isMobile) {
		return (
			<SheetFooter className={className} {...props}>
				{children}
			</SheetFooter>
		)
	}

	return (
		<DialogFooter className={className} {...props}>
			{children}
		</DialogFooter>
	)
}

function ResponsiveDialogTitle({
	className,
	children,
	...props
}: ResponsiveDialogTitleProps) {
	const isMobile = useIsMobile()
	const titleClass =
		"text-center line-clamp-2  font-['DINPro'] text-bold text-base md:text-lg leading-none focus:outline-none"

	if (isMobile) {
		return (
			<SheetTitle className={cn(titleClass, className)} {...props}>
				{children}
			</SheetTitle>
		)
	}

	return (
		<DialogTitle className={cn(titleClass, className)} {...props}>
			{children}
		</DialogTitle>
	)
}

function ResponsiveDialogDescription({
	className,
	children,
	...props
}: ResponsiveDialogDescriptionProps) {
	const isMobile = useIsMobile()

	if (isMobile) {
		return (
			<SheetDescription className={className} {...props}>
				{children}
			</SheetDescription>
		)
	}

	return (
		<DialogDescription className={className} {...props}>
			{children}
		</DialogDescription>
	)
}

export {
	ResponsiveDialog,
	ResponsiveDialogContent,
	ResponsiveDialogDescription,
	ResponsiveDialogFooter,
	ResponsiveDialogHeader,
	ResponsiveDialogTitle,
	ResponsiveDialogTrigger,
}
